import { computed, ref } from 'vue';

/**
 * Таблица во весь экран.
 *
 * Таблица — то, в чём человек проводит день, а над ней стоят две полосы:
 * общая шапка приложения и панель самой таблицы. Это 96 пикселей, то есть
 * ещё две-три строки кандидатов на ноутбуке — и они заметны, когда идёшь
 * по списку сверху вниз.
 *
 * Поэтому режим, в котором остаётся только таблица: общая шапка прячется,
 * а браузер уходит в полный экран. Отдельного «мобильного вида» у таблицы
 * нет и не будет — таблица должна оставаться таблицей, как в Гугл-таблицах;
 * это единственный режим показа, который ей нужен.
 *
 * Состояние живёт в модуле, а не в компоненте: его читает и каркас
 * приложения (прячет шапку), и сама таблица (меняет кнопку).
 */
const enabled = ref(false);

/** Уход из полного экрана мимо нашей кнопки — Esc, F11, кнопка браузера. */
let watching = false;

function watchBrowserExit(): void {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  document.addEventListener('fullscreenchange', () => {
    // Браузер вышел сам — состояние обязано это заметить, иначе шапка
    // останется спрятанной, а выйти будет нечем
    if (!document.fullscreenElement) enabled.value = false;
  });
}

export function useTableFullscreen() {
  watchBrowserExit();

  async function toggle(): Promise<void> {
    const next = !enabled.value;
    enabled.value = next;

    try {
      if (next) await document.documentElement.requestFullscreen();
      else if (document.fullscreenElement) await document.exitFullscreen();
    } catch {
      // Полный экран может быть запрещён политикой окна или отклонён
      // пользователем. Это не повод отказываться от режима: шапку мы
      // прячем сами, и строк всё равно становится больше.
    }
  }

  return { enabled: computed(() => enabled.value), toggle };
}
